// services/inventoryApi.js
// ---------------------------------------------------------------------------
// Admin-only inventory management API
// All endpoints require: Authorization: Bearer <token>  +  X-Tenant-ID header
//
// GET    /api/pharmacy/inventory/products        → all products (incl. out-of-stock) — NOT paginated, avoid at scale
// GET    /api/pharmacy/inventory/products/page    → paginated + filtered admin listing (preferred)
// GET    /api/pharmacy/inventory/products/stats   → { total, inStock, outOfStock, prescriptionRequired }
// GET    /api/pharmacy/categories               → all categories
// POST   /api/pharmacy/products                 → create product  (admin)
// PUT    /api/pharmacy/products/:id             → update product  (admin)
// DELETE /api/pharmacy/products/:id             → delete product  (admin)
// PATCH  /api/pharmacy/products/:id/stock       → toggle in-stock (admin)
// POST   /api/pharmacy/inventory/products/bulk-upload  → bulk CSV upload (admin)
// ---------------------------------------------------------------------------

import { API_BASE_URL as BASE_URL } from './apiBaseUrl';

const authHeaders = (token, tenantId) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
  'X-Tenant-ID': tenantId,
});

// ---------------------------------------------------------------------------
// Fetch ALL products for admin (incl. out-of-stock)
// Uses /inventory/products which returns a plain List — no pagination.
// NOTE: at 124k+ products this loads and serializes every row in one
// response and will hang the page. Kept only for very small catalogs /
// backward compatibility — use getProductsPage() + getInventoryStats() below.
// ---------------------------------------------------------------------------
export const getAllProducts = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/pharmacy/inventory/products`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`Failed to fetch products (${res.status})`);
  return res.json(); // plain array
};

// ---------------------------------------------------------------------------
// Paginated inventory listing — GET /api/pharmacy/inventory/products/page
// Required at 124k-row scale: filtering/searching/paging all happen on the
// backend now instead of loading the whole catalog into the browser.
// Returns { items, totalPages, totalElements }.
// ---------------------------------------------------------------------------
export const getProductsPage = async (token, tenantId, {
  categoryId,
  medicineType,
  q,
  inStock,      // true | false | undefined (undefined = both)
  prescription, // true | false | undefined (undefined = both) — powers the
                // "Prescription req" stat-card CTA on Inventory Management
  page = 0,
  size = 50,
} = {}) => {
  const params = new URLSearchParams();
  if (categoryId   && categoryId   !== 'all') params.set('categoryId',   categoryId);
  if (medicineType && medicineType !== 'all') params.set('medicineType', medicineType);
  if (q)                                       params.set('q', q);
  if (inStock !== undefined && inStock !== null) params.set('inStock', inStock);
  if (prescription !== undefined && prescription !== null) params.set('prescription', prescription);
  params.set('page', page);
  params.set('size', size);

  const res = await fetch(`${BASE_URL}/pharmacy/inventory/products/page?${params}`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`Failed to fetch products (${res.status})`);

  const data = await res.json();
  return {
    items: data.content ?? [],
    totalPages: data.totalPages ?? 1,
    totalElements: data.totalElements ?? (data.content ?? []).length,
  };
};

// ---------------------------------------------------------------------------
// Dashboard stat cards — GET /api/pharmacy/inventory/products/stats
// Computed via COUNT queries on the backend rather than derived client-side
// from a fully-loaded product list (required at 124k rows).
// Returns { total, inStock, outOfStock, prescriptionRequired }.
// ---------------------------------------------------------------------------
export const getInventoryStats = async (token, tenantId, { categoryId, medicineType } = {}) => {
  const params = new URLSearchParams();
  if (categoryId   && categoryId   !== 'all') params.set('categoryId',   categoryId);
  if (medicineType && medicineType !== 'all') params.set('medicineType', medicineType);

  const res = await fetch(`${BASE_URL}/pharmacy/inventory/products/stats?${params}`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`Failed to fetch inventory stats (${res.status})`);
  return res.json();
};


// ---------------------------------------------------------------------------
// Fetch all categories (needed for create/edit forms)
// ---------------------------------------------------------------------------
export const getInventoryCategories = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/pharmacy/categories`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`Failed to fetch categories (${res.status})`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Create a new product
// ---------------------------------------------------------------------------
export const createProduct = async (token, tenantId, payload) => {
  const res = await fetch(`${BASE_URL}/pharmacy/products`, {
    method: 'POST',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to create product (${res.status})`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Update an existing product (full replace)
// ---------------------------------------------------------------------------
export const updateProduct = async (token, tenantId, id, payload) => {
  const res = await fetch(`${BASE_URL}/pharmacy/products/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to update product (${res.status})`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Delete a product
// ---------------------------------------------------------------------------
export const deleteProduct = async (token, tenantId, id) => {
  const res = await fetch(`${BASE_URL}/pharmacy/products/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`Failed to delete product (${res.status})`);
};

// ---------------------------------------------------------------------------
// Toggle stock status (PATCH /products/:id/stock)
// When marking in stock with stockQuantity=0, pass stockQuantity=1 to keep
// in_stock and stock_quantity in sync and avoid the out-of-sync bug.
// ---------------------------------------------------------------------------
export const toggleStock = async (token, tenantId, id, inStock, stockQuantity) => {
  const body = { inStock };
  if (stockQuantity != null) body.stockQuantity = stockQuantity;
  const res = await fetch(`${BASE_URL}/pharmacy/products/${id}/stock`, {
    method: 'PATCH',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to update stock status (${res.status})`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Product image (S3) — POST/DELETE /products/:id/image
// Only usable once the product already exists (has an id), since the backend
// endpoint is keyed by product id — so image upload happens as a second step
// right after create, or any time from the edit modal.
// ---------------------------------------------------------------------------
export const uploadProductImage = async (token, tenantId, id, file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE_URL}/pharmacy/products/${id}/image`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
      // no Content-Type — browser sets the multipart boundary
    },
    body: form,
  });
  if (!res.ok) throw new Error(`Failed to upload image (${res.status})`);
  return res.json();
};

export const deleteProductImage = async (token, tenantId, id) => {
  const res = await fetch(`${BASE_URL}/pharmacy/products/${id}/image`, {
    method: 'DELETE',
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`Failed to remove image (${res.status})`);
};

// ---------------------------------------------------------------------------
// Medicine type options — matches backend enum values
// ---------------------------------------------------------------------------
export const MEDICINE_TYPES = [
  { value: 'allopathy',  label: 'Allopathy',  icon: '💊' },
  { value: 'homeopathy', label: 'Homeopathy', icon: '🌿' },
  { value: 'ayurveda',   label: 'Ayurveda',   icon: '🍃' },
];

// ---------------------------------------------------------------------------
// Bulk import — POST /api/pharmacy/inventory/products/bulk-upload
// Sends the raw CSV file as multipart/form-data.
// Backend parses the CSV and returns List<ProductDetailResponse>.
//
// NOTE: CSV must have exactly 13 columns in this order:
//   name, brand, categoryId, price, mrp, unit, inStock, stockQuantity,
//   prescription, emoji, tags (pipe-separated), description, medicineType
// ---------------------------------------------------------------------------
export const bulkImportProducts = async (token, tenantId, file) => {
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${BASE_URL}/pharmacy/inventory/products/bulk-upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
      // Do NOT set Content-Type — browser sets multipart boundary automatically
    },
    body: form,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  return res.json(); // List<ProductDetailResponse>
};

// ---------------------------------------------------------------------------
// Full dataset import — POST /api/pharmacy/inventory/products/bulk-import-dataset
// For the new ~124k-row pharmacy dataset (28-column format), NOT the legacy
// 13-column bulk-upload above. Sends the raw CSV as multipart/form-data and
// uses fetch's native streaming upload, so large files (100s of MB) don't
// need to be loaded into a JS string first — the browser streams the File
// object directly from disk.
//
// NOTE: CSV must have exactly 28 columns in this order:
//   id, name, brand, category_id, price, mrp, unit, rating, review_count,
//   in_stock, stock_quantity, prescription, emoji, description, medicine_type,
//   packaging, dosage_form, composition, alcohol_interaction, pregnancy_interaction,
//   expert_advice, common_side_effects, how_to_use, how_it_works, faq_for_medicine,
//   medicine_interaction, product_use, image_file_name
//
// Returns { inserted: <number> }.
// ---------------------------------------------------------------------------
export const bulkImportDataset = async (token, tenantId, file, { onProgress } = {}) => {
  // Use XHR instead of fetch so we can report upload progress for a
  // multi-hundred-MB file — fetch has no native upload-progress event.
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE_URL}/pharmacy/inventory/products/bulk-import-dataset`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('X-Tenant-ID', tenantId);
    // Do NOT set Content-Type — the browser sets the multipart boundary automatically

    if (onProgress) {
      // Throttle to at most ~5 updates/sec — a 400MB upload can fire dozens of
      // native progress events per second, and re-rendering React on every one
      // of those is exactly what makes the tab feel like it's freezing.
      let lastReported = -1;
      let lastReportedAt = 0;
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const pct = Math.round((e.loaded / e.total) * 100);
        const now = Date.now();
        if (pct === lastReported) return;
        if (pct < 100 && now - lastReportedAt < 200) return;
        lastReported = pct;
        lastReportedAt = now;
        onProgress(pct);
      };
    }

    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error body */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body); // { inserted: <number> }
      } else {
        reject(new Error(body.message || `HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));

    xhr.send(form);
  });
};

// ---------------------------------------------------------------------------
// Async full dataset import — starts a background job and polls for status,
// instead of holding one HTTP request open for the whole 1-3 minute import.
//
// Why: in production this app sits behind CloudFront, which enforces its own
// origin-response timeout (30s by default, 180s max) independent of anything
// the Spring Boot app does. A single long-lived POST for a 100k+ row import
// gets killed by CloudFront with a 504 before the backend ever finishes —
// even though the import itself would have succeeded. Since CloudFront/infra
// config isn't something this app controls, the fix is on the app side:
// the upload request returns almost immediately (just spools the file to
// disk), the real work happens in a background thread, and the frontend
// polls a status endpoint instead of waiting on one request.
//
// POST /api/pharmacy/inventory/products/bulk-import-dataset/start   → { jobId }
// GET  /api/pharmacy/inventory/products/bulk-import-dataset/status/:jobId
//      → { status: 'PROCESSING'|'COMPLETE'|'FAILED', inserted?, skipped?, error? }
// ---------------------------------------------------------------------------
export const startBulkImportDataset = async (token, tenantId, file, { onProgress } = {}) => {
  // XHR (not fetch) so we can report upload progress for a multi-hundred-MB
  // file — fetch has no native upload-progress event. This part still needs
  // to finish uploading the bytes, but that's normally fast; it's the
  // server-side DB insert afterward that used to take minutes inside the
  // same request. Now that insert happens after this call already returned.
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE_URL}/pharmacy/inventory/products/bulk-import-dataset/start`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('X-Tenant-ID', tenantId);
    // Do NOT set Content-Type — the browser sets the multipart boundary automatically

    if (onProgress) {
      let lastReported = -1;
      let lastReportedAt = 0;
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const pct = Math.round((e.loaded / e.total) * 100);
        const now = Date.now();
        if (pct === lastReported) return;
        if (pct < 100 && now - lastReportedAt < 200) return;
        lastReported = pct;
        lastReportedAt = now;
        onProgress(pct);
      };
    }

    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error body */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body); // { jobId }
      } else {
        reject(new Error(body.message || `HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));

    xhr.send(form);
  });
};

export const getBulkImportDatasetStatus = async (token, tenantId, jobId) => {
  const res = await fetch(
    `${BASE_URL}/pharmacy/inventory/products/bulk-import-dataset/status/${jobId}`,
    { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': tenantId } }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  return res.json(); // { status, inserted?, skipped?, error? }
};

/**
 * Convenience wrapper: starts the import, then polls status every
 * `pollIntervalMs` until COMPLETE or FAILED. Resolves with { inserted, skipped }
 * or rejects with an Error carrying the server's error message.
 */
export const runBulkImportDataset = async (
  token, tenantId, file, { onProgress, onStatusChange, pollIntervalMs = 2000 } = {}
) => {
  const { jobId } = await startBulkImportDataset(token, tenantId, file, { onProgress });

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const status = await getBulkImportDatasetStatus(token, tenantId, jobId);
        if (onStatusChange) onStatusChange(status);

        if (status.status === 'COMPLETE') {
          resolve({ inserted: status.inserted ?? 0, skipped: status.skipped ?? 0 });
        } else if (status.status === 'FAILED') {
          reject(new Error(status.error || 'Import failed'));
        } else {
          setTimeout(poll, pollIntervalMs);
        }
      } catch (e) {
        reject(e);
      }
    };
    poll();
  });
};

// ---------------------------------------------------------------------------
// Export — triggers a CSV download from the already-loaded product list
// (no extra API call needed)
// ---------------------------------------------------------------------------
export const exportProductsAsCSV = (products) => {
  const headers = ['Name','Brand','Category','Price','MRP','Unit','In Stock','Prescription','Medicine Type'];
  const rows = products.map(p => [
    p.name, p.brand, p.categoryName ?? p.categoryId, p.price, p.mrp,
    p.unit, p.inStock ? 'Yes' : 'No', p.prescription ? 'Yes' : 'No', p.medicineType ?? '',
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'inventory.csv'; a.click();
  URL.revokeObjectURL(url);
};